import { SwapSessao } from './swap';

/**
 * Modelo de UI da autorização manual (lista, cadastro, repetir).
 *
 * Espelha os campos relevantes de `SwapSessao` em nomes "amigáveis"
 * (camelCase em PT-BR, sem prefixos do banco), seguindo a convenção
 * predominante no app (ver `IMotoristaModel`, `IFrotaModel`,
 * `LoginModel.Retorno`).
 *
 * O DTO da API é `SwapSessao` (estilo espelho do banco). A conversão
 * é feita pela função `toAutorizacaoManualModel`.
 */
export class AutorizacaoManualModel {
  constructor(
    /** Sequencial dentro da página (somente listagem). */
    public numero: number | null,
    /** PK numérica da sessão. */
    public codSessao: number | null,
    /** Identificador externo da sessão (`DSC_SESSION_EXTERNA`). */
    public dscSessionExterna: string,
    /** ISO datetime — data de criação da sessão. */
    public dataCriacao: string,
    /** ISO datetime — validade da autorização manual (retornada pelo backend). */
    public dataValidade: string,
    /** Etapa da sessão SWAP (`ETAPA` / proc). */
    public etapa: string,
    public codMotorista: number | null,
    public nomeMotorista: string,
    /** Apenas dígitos. */
    public cpfMotorista: string,
    public celularMotorista: string,
    public placa: string,
    /** Cartão mascarado (somente em consulta). */
    public cartaoMascarado: string,
    /** Código do produto (combustível). */
    public cdProduto: string,
    public combustivel: string,
    public precoUnitario: number,
    public litros: number,
    public valorTotal: number,
    public hodometro: number,
    public autorizado: boolean | null,
    /**
     * Espelho do booleano de bypass vindo da API (`habilitaByPass` no JSON).
     * Mantido por compatibilidade com telas/código que ainda leem este nome.
     */
    public habilitaByPass: boolean,
    /**
     * Mesmo significado que {@link habilitaByPass}: controla bypass na grade
     * (`bypassDisponivel`). Preenchido a partir do mesmo valor normalizado do DTO.
     */
    public flgPermiteBypass: boolean,
    public codAutorizacao: string | null,
    public codAbastecimento: number | null,
    /** ISO datetime — última atividade (`DTH_ULT_ATIVIDADE`). */
    public dataUltimaAtividade: string,
    public codErroAuditoria: string | null,
    public msgErroAuditoria: string | null,
    /** Código MCC da autorização (null se ainda não existir). */
    public codMcc: string | null,
    /** Descrição do MCC (associada ao `codMcc`). null se não houver. */
    public dscMcc: string | null,
    /** Código do posto da autorização (DTO `codPosto`). */
    public codPosto: string,
    /** Nome do posto (DTO `nomePosto`). */
    public nomePosto: string,
    /** True se a sessão tem imagem da bomba registrada. */
    public flgImgBomba: boolean,
    /** True se a sessão tem imagem do painel/odômetro registrada. */
    public flgImgPainel: boolean,
    /** True se a sessão tem imagem da placa registrada. */
    public flgImgPlaca: boolean,
  ) {}

  /** Erro de auditoria: mensagem preenchida em transação não autorizada. */
  get temErro(): boolean {
    if (this.autorizado === true) {
      return false;
    }
    return (this.msgErroAuditoria ?? '').trim() !== '';
  }

  /** Bypass na engrenagem e botão da grade: só com `flgPermiteBypass`. */
  get bypassDisponivel(): boolean {
    return this.flgPermiteBypass === true;
  }

  /** Repetir na engrenagem: somente quando autorizado = Nao (`false`). NULL nao repete. */
  get repetirDisponivel(): boolean {
    return this.autorizado === false;
  }

  get isAutorized(): string {
    return this.autorizado ? 'Sim' : 'Não';
  }

  private dataFormatada(dataStr: string | null): string {
    if (!dataStr) {
      return '';
    }
    try {
      const date = new Date(dataStr);
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dataStr;
    }
  }

  get dataCriacaoFormatada(): string {
    return this.dataFormatada(this.dataCriacao);
  }

  get dataValidadeFormatada(): string {
    return this.dataFormatada(this.dataValidade);
  }

  get cpfFormatado(): string {
    const d = (this.cpfMotorista ?? '').replace(/\D/g, '');
    if (d.length !== 11) {
      return this.cpfMotorista ?? '';
    }
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  }

  get celularFormatado(): string {
    const d = (this.celularMotorista ?? '').replace(/\D/g, '');
    if (d.length < 10) {
      return this.celularMotorista ?? '';
    }
    const dd = d.slice(0, 2);
    const rest = d.slice(2);
    if (rest.length === 9) {
      return `(${dd}) ${rest.slice(0, 5)}-${rest.slice(5)}`;
    }
    return `(${dd}) ${rest}`;
  }
}

/**
 * Lê posto para o modelo de UI. Prioridade: JSON da API (`codPosto` / `nomePosto`
 * vindos de `CodPosto` / `NomePosto` no C#). Aceita tambem os nomes das colunas
 * retornadas pelas procs (`COD_POSTO`, `PA7_NOME`).
 */
function strCampoSwap(obj: object, chavesPreferenciais: string[]): string {
  const o = obj as Record<string, unknown>;
  for (const k of chavesPreferenciais) {
    if (!Object.prototype.hasOwnProperty.call(o, k)) {
      continue;
    }
    const v = o[k];
    if (v == null) {
      continue;
    }
    const s = String(v).trim();
    if (s !== '') {
      return s;
    }
  }
  const nomes = Object.keys(o);
  for (const k of chavesPreferenciais) {
    const found = nomes.find((n) => n.toLowerCase() === k.toLowerCase());
    if (found == null) {
      continue;
    }
    const v = o[found];
    if (v == null) {
      continue;
    }
    const s = String(v).trim();
    if (s !== '') {
      return s;
    }
  }
  return '';
}

function extrairPostoSwap(swap: SwapSessao): { codPosto: string; nomePosto: string } {
  return {
    codPosto: strCampoSwap(swap, [
      'codPosto',
      'CodPosto',
      'COD_POSTO',
    ]),
    nomePosto: strCampoSwap(swap, [
      'nomePosto',
      'NomePosto',
      'PA7_NOME',
      'pa7Nome',
      'Pa7Nome',
    ]),
  };
}

/** S, SIM, true, 1, Y → true; demais falsy conhecidos → false. */
function flagVerdadeiro(v: unknown): boolean {
  if (v === true || v === 1) {
    return true;
  }
  if (v === false || v === 0 || v == null) {
    return false;
  }
  const s = String(v).trim().toUpperCase();
  return ['S', 'SIM', 'TRUE', '1', 'Y'].includes(s);
}

/**
 * Boolean "permite bypass" a partir do DTO `SwapSessao`.
 * Fonte atual na API: `habilitaByPass` (coluna `HabilitaByPass` na proc).
 * Ainda aceita chaves antigas (`flgPermiteBypass`, `FLG_PERMITE_BYPASS`, snake_case)
 * em respostas ou mocks legados.
 */
function extrairFlgPermiteBypass(swap: SwapSessao): boolean {
  const o = swap as unknown as Record<string, unknown>;
  const canonical = ['habilitaByPass', 'HabilitaByPass'];
  const legacy = [
    'flgPermiteBypass',
    'FLG_PERMITE_BYPASS',
    'FlgPermiteBypass',
    'flg_permite_bypass',
  ];
  for (const group of [canonical, legacy]) {
    for (const k of group) {
      if (Object.prototype.hasOwnProperty.call(o, k)) {
        return flagVerdadeiro(o[k]);
      }
    }
    for (const k of group) {
      const found = Object.keys(o).find((n) => n.toLowerCase() === k.toLowerCase());
      if (found != null) {
        return flagVerdadeiro(o[found]);
      }
    }
  }
  return false;
}

function interpretarAutorizadoValor(raw: unknown): boolean | null {
  if (raw === undefined || raw === null || raw === '') {
    return null;
  }
  if (typeof raw === 'boolean') {
    return raw;
  }
  if (typeof raw === 'number') {
    if (raw === 1) {
      return true;
    }
    if (raw === 0) {
      return false;
    }
  }
  const s = String(raw).trim().toUpperCase();
  if (['S', 'SIM', 'TRUE', '1', 'Y'].includes(s)) {
    return true;
  }
  if (['N', 'NAO', 'NÃO', 'FALSE', '0'].includes(s)) {
    return false;
  }
  return null;
}

/**
 * Normaliza `FLG_AUTORIZADO` / `flgAutorizado` (bool, S/N, SIM/NAO, 0/1).
 */
function extrairFlgAutorizado(swap: SwapSessao): boolean | null {
  const o = swap as unknown as Record<string, unknown>;
  const candidatos = ['flgAutorizado', 'FLG_AUTORIZADO', 'FlgAutorizado', 'flg_autorizado'];
  for (const k of candidatos) {
    if (Object.prototype.hasOwnProperty.call(o, k)) {
      return interpretarAutorizadoValor(o[k]);
    }
  }
  for (const k of candidatos) {
    const found = Object.keys(o).find((n) => n.toLowerCase() === k.toLowerCase());
    if (found != null) {
      return interpretarAutorizadoValor(o[found]);
    }
  }
  return null;
}

/** MCC da autorização (abastecimento realizado), independente de erro de auditoria. */
function extrairMccSwap(swap: SwapSessao): {
  codMcc: string | null;
  dscMcc: string | null;
} {
  const mcc = (swap.codMcc ?? '').trim();
  if (!mcc) {
    return { codMcc: null, dscMcc: null };
  }
  const dsc = (swap.dscMcc ?? '').trim();
  return {
    codMcc: mcc,
    dscMcc: dsc || null,
  };
}

/** Código 1 / texto APROVADO = auditoria ok, sem erro na UI. */
function isAuditoriaAprovada(codErr: string, msg: string): boolean {
  if (codErr === '1' || Number(codErr) === 1) {
    return true;
  }
  return msg.toUpperCase() === 'APROVADO';
}

/**
 * Erro de auditoria (`MSG_ERRO_AUDITORIA` / `DSC_RESTRICAO_AUDITORIA` na proc).
 * Independente do MCC.
 */
function extrairErroAuditoriaSwap(swap: SwapSessao): {
  codErroAuditoria: string | null;
  msgErroAuditoria: string | null;
} {
  const msg = (swap.msgErroAuditoria ?? '').trim();
  const codErr = (swap.codErroAuditoria ?? '').trim();
  if (isAuditoriaAprovada(codErr, msg)) {
    return { codErroAuditoria: null, msgErroAuditoria: null };
  }
  if (!msg && !codErr) {
    return { codErroAuditoria: null, msgErroAuditoria: null };
  }
  return {
    codErroAuditoria: codErr || null,
    msgErroAuditoria: msg || null,
  };
}

/**
 * Converte um `SwapSessao` (DTO da API) para `AutorizacaoManualModel`
 * (modelo de UI). Campos ausentes recebem default neutro.
 */
export function toAutorizacaoManualModel(swap: SwapSessao): AutorizacaoManualModel {
  const mcc = extrairMccSwap(swap);
  const aud = extrairErroAuditoriaSwap(swap);
  const posto = extrairPostoSwap(swap);
  const permiteBypass = extrairFlgPermiteBypass(swap);
  return new AutorizacaoManualModel(
    swap.numControle ?? null,
    swap.codSessao ?? null,
    swap.dscSessionExterna ?? '',
    swap.dthCriacao ?? '',
    swap.datValidadePreautorizacao ?? '',
    swap.etapa ?? '',
    swap.codMotorista ?? null,
    swap.dscNomeMotorista ?? '',
    (swap.numCpfMotorista ?? '').replace(/\D/g, ''),
    swap.numTelMotorista ?? '',
    swap.dscPlaca ?? '',
    swap.dscCartao ?? '',
    swap.cdProduto ?? '',
    swap.dscCombustivel ?? '',
    Number(swap.vlrPrecoUnitario ?? 0),
    Number(swap.qtdVolume ?? 0),
    Number(swap.vlrTotal ?? 0),
    Number(swap.numKm ?? 0),
    extrairFlgAutorizado(swap),
    permiteBypass,
    permiteBypass,
    swap.codAutorizacao ?? null,
    swap.codAbastecimento ?? null,
    swap.dthUltAtividade ?? '',
    aud.codErroAuditoria,
    aud.msgErroAuditoria,
    mcc.codMcc,
    mcc.dscMcc,
    posto.codPosto,
    posto.nomePosto,
    swap.flgImgBomba === true,
    swap.flgImgPainel === true,
    swap.flgImgPlaca === true,
  );
}
